import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Pencil, Trash2, Send, FileCheck, CreditCard, Copy, Building2, User, Download, Banknote } from "lucide-react"
import { useInvoice, useDeleteInvoice, useIssueInvoice, useSendInvoice, useRecordPayment, useCreateCorrectiveInvoice } from "@/hooks/use-invoices"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { formatDate, formatCurrency } from "@/lib/utils"
import { toast } from "sonner"
import type { InvoiceStatus, InvoiceType } from "@/lib/types"

const STATUS_CONFIG: Record<InvoiceStatus, { label: string; color: string; bg: string }> = {
  draft: { label: "Szkic", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
  issued: { label: "Wystawiona", color: "#3B82F6", bg: "rgba(59, 130, 246, 0.1)" },
  sent: { label: "Wyslana", color: "#8B5CF6", bg: "rgba(139, 92, 246, 0.1)" },
  paid: { label: "Zaplacona", color: "#10B981", bg: "rgba(16, 185, 129, 0.1)" },
  partially_paid: { label: "Czesciowo", color: "#F59E0B", bg: "rgba(245, 158, 11, 0.1)" },
  overdue: { label: "Zalegla", color: "#EF4444", bg: "rgba(239, 68, 68, 0.1)" },
  cancelled: { label: "Anulowana", color: "#6B7280", bg: "rgba(107, 114, 128, 0.1)" },
  corrected: { label: "Skorygowana", color: "#6366F1", bg: "rgba(99, 102, 241, 0.1)" },
}

const TYPE_LABELS: Record<InvoiceType, string> = {
  vat: "Faktura VAT",
  proforma: "Faktura proforma",
  corrective: "Faktura korygujaca",
  margin: "Faktura marza",
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  transfer: "Przelew",
  cash: "Gotowka",
  card: "Karta",
  other: "Inne",
}

export function InvoiceDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data, isLoading } = useInvoice(id)
  const deleteMutation = useDeleteInvoice()
  const issueMutation = useIssueInvoice()
  const sendMutation = useSendInvoice()
  const paymentMutation = useRecordPayment()
  const correctiveMutation = useCreateCorrectiveInvoice()

  const [showPaymentDialog, setShowPaymentDialog] = useState(false)
  const [paymentAmount, setPaymentAmount] = useState("")
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split("T")[0])
  const [paymentMethod, setPaymentMethod] = useState("transfer")

  const invoice = data?.data

  const handleDelete = async () => {
    if (!id || !confirm("Czy na pewno chcesz usunac te fakture?")) return
    await deleteMutation.mutateAsync(id)
    toast.success("Faktura usunieta")
    navigate("/invoices")
  }

  const handleIssue = async () => {
    if (!id) return
    await issueMutation.mutateAsync(id)
    toast.success("Faktura wystawiona")
  }

  const handleSend = async () => {
    if (!id) return
    await sendMutation.mutateAsync(id)
    toast.success("Faktura wyslana")
  }

  const handleRecordPayment = async () => {
    if (!id || !paymentAmount) return
    await paymentMutation.mutateAsync({
      id,
      data: {
        amount: parseFloat(paymentAmount),
        payment_date: paymentDate,
        payment_method: paymentMethod,
      },
    })
    setShowPaymentDialog(false)
    setPaymentAmount("")
    toast.success("Platnosc zarejestrowana")
  }

  const handleCorrective = async () => {
    if (!id) return
    const reason = prompt("Podaj powod korekty:")
    if (!reason) return
    const res = await correctiveMutation.mutateAsync({ id, data: { correction_reason: reason } })
    toast.success("Utworzono fakture korygujaca")
    navigate(`/invoices/${res.data.id}`)
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!invoice) {
    return <div className="text-muted-foreground">Nie znaleziono faktury</div>
  }

  const statusCfg = STATUS_CONFIG[invoice.status]
  const fmt = (n: number) => new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
  const paymentProgress = invoice.total_gross > 0 ? Math.min((invoice.amount_paid / invoice.total_gross) * 100, 100) : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/invoices")} className="rounded-md p-1.5 transition-colors hover:bg-muted/30 text-muted-foreground">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="page-title">{invoice.number}</h1>
              <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                {TYPE_LABELS[invoice.invoice_type]}
              </span>
              <Badge variant="outline" style={{ color: statusCfg.color, backgroundColor: statusCfg.bg, borderColor: statusCfg.color }}>
                {statusCfg.label}
              </Badge>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`/api/v1/invoices/${id}/pdf`, "_blank")}>
            <Download size={14} className="mr-1" /> PDF
          </Button>
          {invoice.status === "draft" && (
            <Button variant="outline" size="sm" onClick={handleIssue}>
              <FileCheck size={14} className="mr-1" /> Wystaw
            </Button>
          )}
          {(invoice.status === "issued" || invoice.status === "draft") && (
            <Button variant="outline" size="sm" onClick={handleSend}>
              <Send size={14} className="mr-1" /> Wyslij
            </Button>
          )}
          {["issued", "sent", "partially_paid", "overdue"].includes(invoice.status) && (
            <Button variant="outline" size="sm" onClick={() => { setPaymentAmount(String(invoice.amount_due)); setShowPaymentDialog(true) }}>
              <CreditCard size={14} className="mr-1" /> Platnosc
            </Button>
          )}
          {["issued", "sent", "paid"].includes(invoice.status) && (
            <Button variant="outline" size="sm" onClick={handleCorrective}>
              <Copy size={14} className="mr-1" /> Korekta
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => navigate(`/invoices/${id}/edit`)}>
            <Pencil size={14} className="mr-1" /> Edytuj
          </Button>
          <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-600">
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2">
          <Tabs defaultValue="items">
            <TabsList>
              <TabsTrigger value="items">Pozycje ({invoice.items.length})</TabsTrigger>
              <TabsTrigger value="payments">Platnosci ({invoice.payments.length})</TabsTrigger>
              <TabsTrigger value="details">Szczegoly</TabsTrigger>
            </TabsList>

            <TabsContent value="items" className="mt-4">
              <div className="surface-card">
                {invoice.items.length === 0 ? (
                  <p className="p-8 text-center text-sm text-muted-foreground">Brak pozycji</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Pozycja</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Ilosc</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Cena netto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Rabat</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">VAT</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Netto</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Brutto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.items.map((item) => (
                        <tr key={item.id} className="border-t">
                          <td className="px-4 py-2">
                            <span className="text-foreground">{item.name}</span>
                            {item.pkwiu && <span className="text-xs ml-2 text-muted-foreground">PKWiU: {item.pkwiu}</span>}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {item.quantity} {item.unit}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {fmt(item.unit_price_net)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {item.discount_percent > 0 ? `${item.discount_percent}%` : "\u2014"}
                          </td>
                          <td className="px-4 py-2 text-right font-mono text-muted-foreground">
                            {item.vat_rate}%
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-medium text-foreground">
                            {fmt(item.line_total_net)}
                          </td>
                          <td className="px-4 py-2 text-right font-mono font-medium text-foreground">
                            {fmt(item.line_total_gross)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted">
                        <td colSpan={5} className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">Suma netto:</td>
                        <td className="px-4 py-2 text-right font-mono font-semibold text-foreground">{formatCurrency(invoice.total_net, invoice.currency)}</td>
                        <td></td>
                      </tr>
                      <tr className="bg-muted">
                        <td colSpan={5} className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">VAT:</td>
                        <td className="px-4 py-2 text-right font-mono text-muted-foreground">{formatCurrency(invoice.total_vat, invoice.currency)}</td>
                        <td></td>
                      </tr>
                      <tr className="border-t bg-muted">
                        <td colSpan={5} className="px-4 py-2 text-right font-semibold text-foreground">Suma brutto:</td>
                        <td colSpan={2} className="px-4 py-2 text-right font-mono text-base font-bold text-primary">
                          {formatCurrency(invoice.total_gross, invoice.currency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="payments" className="mt-4">
              <div className="surface-card">
                {invoice.payments.length === 0 ? (
                  <p className="p-8 text-center text-sm text-muted-foreground">Brak zarejestrowanych platnosci</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Data</th>
                        <th className="px-4 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Kwota</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Metoda</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Referencja</th>
                        <th className="px-4 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Zarejestrowal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.payments.map((p) => (
                        <tr key={p.id} className="border-t">
                          <td className="px-4 py-2 text-foreground">{formatDate(p.payment_date)}</td>
                          <td className="px-4 py-2 text-right font-mono font-medium text-emerald-500">{fmt(p.amount)} {invoice.currency}</td>
                          <td className="px-4 py-2 text-muted-foreground">{PAYMENT_METHOD_LABELS[p.payment_method || ""] || p.payment_method}</td>
                          <td className="px-4 py-2 text-muted-foreground">{p.reference || "\u2014"}</td>
                          <td className="px-4 py-2 text-muted-foreground">
                            {p.recorded_by ? `${p.recorded_by.first_name} ${p.recorded_by.last_name}` : "\u2014"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </TabsContent>

            <TabsContent value="details" className="mt-4 space-y-4">
              {/* Seller / Buyer */}
              <div className="grid grid-cols-2 gap-4">
                <div className="surface-card p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Sprzedawca</h3>
                  <p className="text-sm font-medium text-foreground">{invoice.seller_name || "\u2014"}</p>
                  {invoice.seller_nip && <p className="text-xs text-muted-foreground">NIP: {invoice.seller_nip}</p>}
                  {invoice.seller_address && <p className="text-sm whitespace-pre-wrap text-muted-foreground">{invoice.seller_address}</p>}
                  {invoice.seller_bank_name && <p className="text-xs mt-2 text-muted-foreground">Bank: {invoice.seller_bank_name}</p>}
                  {invoice.seller_bank_account && <p className="text-xs font-mono text-muted-foreground">{invoice.seller_bank_account}</p>}
                </div>
                <div className="surface-card p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Nabywca</h3>
                  <p className="text-sm font-medium text-foreground">{invoice.buyer_name || "\u2014"}</p>
                  {invoice.buyer_nip && <p className="text-xs text-muted-foreground">NIP: {invoice.buyer_nip}</p>}
                  {invoice.buyer_address && <p className="text-sm whitespace-pre-wrap text-muted-foreground">{invoice.buyer_address}</p>}
                </div>
              </div>
              {invoice.split_payment && (
                <div className="rounded-lg border border-amber-500 bg-amber-500/5 p-3 text-sm">
                  <strong>Mechanizm podzielonej platnosci</strong> &mdash; Faktura objeta obowiazkowym MPP
                </div>
              )}
              {invoice.notes && (
                <div className="surface-card p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Uwagi</h3>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{invoice.notes}</p>
                </div>
              )}
              {invoice.correction_reason && (
                <div className="rounded-lg border border-blue-500 bg-blue-500/5 p-4 space-y-2">
                  <h3 className="text-sm font-semibold text-blue-500">Powod korekty</h3>
                  <p className="text-sm text-muted-foreground">{invoice.correction_reason}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Payment progress */}
          <div className="surface-card p-4 space-y-3">
            <div>
              <span className="text-xs text-muted-foreground">Do zaplaty</span>
              <p className={`text-xl font-mono font-bold ${invoice.amount_due > 0 ? "text-red-500" : "text-emerald-500"}`}>
                {formatCurrency(invoice.amount_due, invoice.currency)}
              </p>
            </div>
            <div className="w-full h-2 rounded-full bg-muted">
              <div className={`h-2 rounded-full transition-all ${paymentProgress >= 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${paymentProgress}%` }} />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Zaplacono: {formatCurrency(invoice.amount_paid, invoice.currency)}</span>
              <span>{Math.round(paymentProgress)}%</span>
            </div>
            <div>
              <span className="text-xs text-muted-foreground">Suma brutto</span>
              <p className="text-lg font-mono font-semibold text-primary">
                {formatCurrency(invoice.total_gross, invoice.currency)}
              </p>
            </div>
          </div>

          {/* Info card */}
          <div className="surface-card p-4 space-y-3">
            {invoice.company && (
              <div>
                <span className="text-xs text-muted-foreground">Kontrahent</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <Building2 size={14} className="text-muted-foreground" />
                  <button onClick={() => navigate(`/companies/${invoice.company!.id}`)} className="text-sm hover:underline text-primary">
                    {invoice.company.name}
                  </button>
                </div>
              </div>
            )}
            {invoice.owner && (
              <div>
                <span className="text-xs text-muted-foreground">Wlasciciel</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <User size={14} className="text-muted-foreground" />
                  <p className="text-sm text-foreground">{invoice.owner.first_name} {invoice.owner.last_name}</p>
                </div>
              </div>
            )}
            <div className="border-t pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-muted-foreground">Data wystawienia</span>
                  <p className="text-sm text-foreground">{invoice.issue_date ? formatDate(invoice.issue_date) : "\u2014"}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Data sprzedazy</span>
                  <p className="text-sm text-foreground">{invoice.sale_date ? formatDate(invoice.sale_date) : "\u2014"}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Termin platnosci</span>
                  <p className="text-sm text-foreground">{invoice.due_date ? formatDate(invoice.due_date) : "\u2014"}</p>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground">Forma platnosci</span>
                  <p className="text-sm text-foreground">{PAYMENT_METHOD_LABELS[invoice.payment_method] || invoice.payment_method}</p>
                </div>
              </div>
            </div>
            {invoice.issued_at && (
              <div>
                <span className="text-xs text-muted-foreground">Wystawiona</span>
                <p className="text-sm text-foreground">{formatDate(invoice.issued_at)}</p>
              </div>
            )}
            {invoice.paid_at && (
              <div>
                <span className="text-xs text-muted-foreground">Zaplacona</span>
                <p className="text-sm text-emerald-500">{formatDate(invoice.paid_at)}</p>
              </div>
            )}
            <div className="border-t pt-3">
              <span className="text-xs text-muted-foreground">Utworzono</span>
              <p className="text-sm text-foreground">{formatDate(invoice.created_at)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Dialog */}
      {showPaymentDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPaymentDialog(false)}>
          <div className="bg-card rounded-lg p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4 text-foreground">
              <Banknote size={20} className="inline mr-2" />
              Rejestracja platnosci
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Kwota</label>
                <input type="number" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)}
                  className="w-full mt-1 rounded-md border px-3 py-2 text-sm text-foreground" />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Data platnosci</label>
                <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full mt-1 rounded-md border px-3 py-2 text-sm text-foreground" />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Forma platnosci</label>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full mt-1 rounded-md border px-3 py-2 text-sm text-foreground">
                  <option value="transfer">Przelew</option>
                  <option value="cash">Gotowka</option>
                  <option value="card">Karta</option>
                  <option value="other">Inne</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Anuluj</Button>
              <Button onClick={handleRecordPayment} disabled={paymentMutation.isPending}>
                <CreditCard size={14} className="mr-1" />
                {paymentMutation.isPending ? "Zapisywanie..." : "Zarejestruj"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
